import MuiBox from '@mui/material/Box';
import MuiCheckbox from '@mui/material/Checkbox';
import MuiFormControlLabel from '@mui/material/FormControlLabel';
import { alpha, type Theme } from '@mui/material/styles';
import type { ReactNode } from 'react';

export interface CheckboxProps {
  checked: boolean;
  // Emits the next checked state, never MUI's synthetic event — same idiom as
  // TextField emitting the string value.
  onChange: (checked: boolean) => void;
  // Real label: FormControlLabel makes the whole row (box + text) the click area
  // and the accessible name, so no caller ever pairs a bare box with loose text.
  label: ReactNode;
  disabled?: boolean;
  // Draws the emphasized opt-in container around the control: 8px surface radius
  // (the one Dialog/Alert/Disclosure share), hairline warning border, pale warning
  // wash. Lives here because no view can compute the alpha tint without importing
  // @mui/material/styles, which the façade lint forbids outside this package.
  highlight?: boolean;
}

const highlightSx = (theme: Theme) => ({
  border: '1px solid',
  borderColor: alpha(theme.palette.warning.main, 0.5),
  backgroundColor: alpha(theme.palette.warning.main, 0.08),
  borderRadius: 2,
  px: 2,
  py: 0.5,
  width: '100%',
});

export function Checkbox({
  checked,
  onChange,
  label,
  disabled = false,
  highlight = false,
}: CheckboxProps) {
  const control = (
    <MuiFormControlLabel
      control={
        <MuiCheckbox
          checked={checked}
          onChange={(event) => {
            onChange(event.target.checked);
          }}
        />
      }
      label={label}
      disabled={disabled}
    />
  );

  if (!highlight) return control;

  return <MuiBox sx={highlightSx}>{control}</MuiBox>;
}
